import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import "./CaisseMobile.css";

const SENDER_WHATSAPP = "56332255";

// Normalise l'image : data URL, base64 ou endpoint image
const getImageSrc = (image, id) => {
  if (image && typeof image === "string") {
    const trimmed = image.trim();
    if (trimmed.startsWith("data:")) return trimmed;
    if (trimmed.length > 50) return `data:image/jpeg;base64,${trimmed}`;
    if (trimmed.startsWith("http") || trimmed.startsWith("/")) return trimmed;
  }
  if (id) return `/api/ProductV/image/${id}`;
  return null;
};

// Extrait l'ID sous forme de chaîne de n'importe quel objet/string de localisation
const extractLocId = (loc) => {
  if (!loc) return null;
  if (typeof loc === "string") return loc;
  if (loc.$oid) return loc.$oid;
  if (loc._id) {
    if (typeof loc._id === "string") return loc._id;
    if (loc._id.$oid) return loc._id.$oid;
    if (typeof loc._id.toString === "function") return loc._id.toString();
  }
  return null;
};



const CaisseMobile = () => {
  const currentUser = useSelector((state) => state.AuthReducer?.user);

  // ── Authentification Caissier & Emplacement ──
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [cashier, setCashier] = useState(null);
  const [pinInput, setPinInput] = useState("");
  const [loginName, setLoginName] = useState("");
  const [loginError, setLoginError] = useState("");
  const [locationName, setLocationName] = useState("Non définie");
  const [locationId, setLocationId] = useState(null);

  // ── Données POS & Produits ──
  const [products, setProducts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("Tous");

  // Variant Modal (Pour choisir parmi les sous-produits d'un produit principal)
  const [selectedParentProduct, setSelectedParentProduct] = useState(null);

  // ── Panier & Règlement ──
  const [cart, setCart] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState("Espece");
  const [clientPhone, setClientPhone] = useState("");
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [clientStatus, setClientStatus] = useState("");
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");

  // ── Remises & Promotions par Localisation ──
  const [availableDiscounts, setAvailableDiscounts] = useState([]);
  const [selectedDiscount, setSelectedDiscount] = useState(null);
  const [customDiscountPct, setCustomDiscountPct] = useState(0);

  // Modale de confirmation de vente et reçu produit
  const [completedSale, setCompletedSale] = useState(null);

  const categoriesRef = React.useRef(null);
  const scrollCat = (dir) => {
    if (categoriesRef.current) {
      categoriesRef.current.scrollBy({ left: dir * 250, behavior: "smooth" });
    }
  };

  // 1. Initialisation de la localisation depuis l'URL ou le compte
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const locParam = params.get("location") || params.get("loc") || params.get("locationName");

    const storedUserStr = localStorage.getItem("user");
    let storedUser = null;
    try {
      if (storedUserStr) storedUser = JSON.parse(storedUserStr);
    } catch (e) { }

    const activeUser = currentUser || storedUser;

    if (activeUser && activeUser.name) {
      setLoginName(activeUser.name);
    }

    if (locParam) {
      setLocationName(locParam);
    }
  }, [currentUser]);

  // 2. Chargement unique des produits et emplacements
  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        const headers = {
          Authorization: token ? `Bearer ${token}` : "",
          Authorised: token || ""
        };

        const [pRes, lRes] = await Promise.all([
          axios.get("/api/ProductV/getProductV", {
            headers,
            maxContentLength: 50 * 1024 * 1024,
            maxBodyLength: 50 * 1024 * 1024
          }),
          axios.get("/api/Locations/getAllLocations", { headers }).catch(() => ({ data: [] })),
        ]);

        if (!isMounted) return;

        const pData = Array.isArray(pRes.data) ? pRes.data : [];
        const locList = Array.isArray(lRes.data) ? lRes.data : [];

        setProducts(pData);
        setLocations(locList);

        // Résolution de l'ID de localisation si on a un nom
        let resolvedLocId = locationId;
        if (!resolvedLocId && locationName && locationName !== "Non définie" && locationName !== "Caisse Global") {
          const found = locList.find(
            (l) => l.name.toLowerCase() === locationName.toLowerCase() ||
              l._id === locationName
          );
          if (found) {
            resolvedLocId = found._id;
            setLocationId(found._id);
          }
        }

        // Charger les remises associées
        const locQuery = resolvedLocId ? `?location=${resolvedLocId}&activeOnly=true` : `?activeOnly=true`;
        const dRes = await axios.get(`/api/discounts${locQuery}`, { headers }).catch(() => ({ data: { discounts: [] } }));
        if (isMounted && dRes.data?.success) {
          setAvailableDiscounts(dRes.data.discounts || []);
        }
      } catch (err) {
        console.error("Erreur chargement POS:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [locationName]);

  // 3. Connexion Caissier par Nom & Code PIN 4 chiffres
  const handleCashierLogin = async (e) => {
    if (e) e.preventDefault();
    setLoginError("");

    if (!pinInput || pinInput.length !== 4) {
      setLoginError("Veuillez saisir votre Code PIN à 4 chiffres.");
      return;
    }

    try {
      const res = await axios.post("/api/auth/verifyPin", {
        pinCode: pinInput,
        name: loginName,
      });

      if (res.data?.success) {
        if (res.data?.token) {
          localStorage.setItem("token", res.data.token);
        }
        const user = res.data.user;
        setCashier(user);
        setIsUnlocked(true);

        let userLocId = null;
        let userLocName = "Caisse Global";

        if (user.localisation) {
          const rawLocId = extractLocId(user.localisation);
          const foundLoc = locations.find(
            (l) => l._id === rawLocId || String(l._id) === String(rawLocId)
          );

          if (foundLoc) {
            userLocName = foundLoc.name;
            userLocId = foundLoc._id;
          } else if (typeof user.localisation === "string") {
            userLocName = user.localisation;
            const foundByName = locations.find(
              (l) => l.name.toLowerCase() === user.localisation.toLowerCase()
            );
            if (foundByName) userLocId = foundByName._id;
          }
        }

        setLocationName(userLocName);
        setLocationId(userLocId);

        if (userLocId) {
          const token = res.data.token || localStorage.getItem("token");
          axios.get(`/api/discounts?location=${userLocId}&activeOnly=true`, {
            headers: { Authorization: token ? `Bearer ${token}` : "", Authorised: token || "" }
          }).then(dRes => {
            if (dRes.data?.success) setAvailableDiscounts(dRes.data.discounts || []);
          }).catch(() => { });
        }

        setPinInput("");
      } else {
        setLoginError(res.data?.message || "Code PIN incorrect");
      }
    } catch (err) {
      setLoginError(err.response?.data?.message || "Code PIN à 4 chiffres invalide");
    }
  };

  const handleLockPOS = () => {
    setIsUnlocked(false);
    setCashier(null);
    setPinInput("");
    setLoginName("");
    setLoginError("");
    setLocationName("Non définie");
    setLocationId(null);
    setProducts([]);
    setCart([]);
    localStorage.removeItem("token");
  };

  // 4. Recherche de client existant par téléphone
  const handlePhoneChange = async (val) => {
    setClientPhone(val);
    if (val.trim().length >= 8) {
      try {
        const res = await axios.get(`/api/Client/byPhone/${val.trim()}`);
        if (res.data?.success && res.data?.client) {
          setClientName(res.data.client.name || "");
          setClientEmail(res.data.client.email || "");
          setClientStatus("Client trouvé ✅");
        }
      } catch (e) {
        // Client non encore enregistré
      }
    }
  };

  const saveClientData = async () => {
    if (!clientPhone) return null;
    try {
      const res = await axios.post("/api/Client/saveOrUpdate", {
        phone: clientPhone,
        name: clientName,
        email: clientEmail,
        locationName,
      });
      if (res.data?.success) {
        setClientStatus("Client sauvegardé ✅");
        return res.data.client;
      }
    } catch (e) {
      console.error("Erreur sauvegarde client :", e);
    }
    return null;
  };

  // 5. Manipulation du Panier
  const addToCart = (product, subProduct = null) => {
    const itemKey = subProduct ? subProduct._id : product._id;
    const itemName = subProduct ? `${product.name} (${subProduct.name})` : product.name;
    const itemPrice = Number(subProduct ? subProduct.price : product.price) || 0;
    const itemImage = subProduct?.image || product.image || null;

    setCart((prevCart) => {
      const existing = prevCart.find((i) => i.id === itemKey);
      if (existing) {
        return prevCart.map((i) =>
          i.id === itemKey ? { ...i, quantity: i.quantity + 1 } : i
        );
      }
      return [
        ...prevCart,
        {
          id: itemKey,
          productId: product._id,
          subProductId: subProduct?._id || null,
          name: itemName,
          parentName: product.name,
          subName: subProduct?.name || null,
          price: itemPrice,
          image: itemImage,
          quantity: 1,
        },
      ];
    });

    if (selectedParentProduct) {
      setSelectedParentProduct(null);
    }
  };

  const updateQuantity = (id, delta) => {
    setCart((prevCart) =>
      prevCart
        .map((item) => {
          if (item.id === id) {
            const newQty = item.quantity + delta;
            return newQty > 0 ? { ...item, quantity: newQty } : null;
          }
          return item;
        })
        .filter(Boolean)
    );
  };

  const removeFromCart = (id) => {
    setCart((prevCart) => prevCart.filter((item) => item.id !== id));
  };

  const clearCart = () => {
    setCart([]);
    setSelectedDiscount(null);
    setCustomDiscountPct(0);
    setClientPhone("");
    setClientName("");
    setClientEmail("");
    setStatusMsg("");
  };

  // Calculs Totaux du Panier & Remises
  const subTotalTTC = useMemo(() => {
    return cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  }, [cart]);

  const activeDiscountPercentage = useMemo(() => {
    if (selectedDiscount) return Number(selectedDiscount.percentage) || 0;
    if (customDiscountPct > 0) return Number(customDiscountPct) || 0;
    return 0;
  }, [selectedDiscount, customDiscountPct]);

  const discountAmount = useMemo(() => {
    if (activeDiscountPercentage > 0 && subTotalTTC > 0) {
      return (subTotalTTC * activeDiscountPercentage) / 100;
    }
    return 0;
  }, [subTotalTTC, activeDiscountPercentage]);

  const totalTTC = useMemo(() => {
    return Math.max(0, subTotalTTC - discountAmount);
  }, [subTotalTTC, discountAmount]);

  const totalHT = totalTTC / 1.07;
  const tva = totalTTC - totalHT;

  // 6. PRODUITS DISPONIBLES POUR CETTE CAISSE
  const availableStoreProducts = useMemo(() => {
    const list = [];

    const matchesLoc = (locField) => {
      // Les articles sans localisation spécifique sont considérés comme communs/globaux
      if (!locField) return true;
      if (!locationName || locationName === "Non définie" || locationName === "Caisse Global") return true;

      const itemLocId = extractLocId(locField);
      if (locationId && itemLocId && String(itemLocId) === String(locationId)) return true;

      const locNameStr = typeof locField === "object" && locField.name
        ? locField.name
        : (locations.find((l) => String(l._id) === String(itemLocId))?.name || String(locField));

      if (locNameStr) {
        const targetStr = locNameStr.toLowerCase().trim();
        const userLocStr = locationName.toLowerCase().trim();
        if (targetStr === userLocStr || targetStr.includes(userLocStr) || userLocStr.includes(targetStr)) return true;
        if ((targetStr.includes("bng") || targetStr.includes("bag")) && (userLocStr.includes("bng") || userLocStr.includes("bag"))) return true;
        if (targetStr.includes("twin") && (userLocStr.includes("twin") || userLocStr.includes("tp-") || userLocStr.includes("tp "))) return true;
        if (targetStr.includes("espace") && (userLocStr.includes("espace") || userLocStr.includes("e-space"))) return true;
        if (targetStr.includes("spintoss") && userLocStr.includes("spintoss")) return true;
        if (targetStr.includes("wellness") && userLocStr.includes("wellness")) return true;
        return false;
      }
      return true;
    };

    products.forEach((p) => {
      if (p.subProductsV && p.subProductsV.length > 0) {
        let validSubProducts = p.subProductsV.filter(
          (sp) => matchesLoc(sp.localisation) && Number(sp.price) > 0
        );

        // Si aucun sous-produit ne correspond strictement, on prend les sous-produits valides
        if (validSubProducts.length === 0) {
          validSubProducts = p.subProductsV.filter((sp) => Number(sp.price) > 0);
        }

        if (validSubProducts.length > 0) {
          const displayPrice = Number(validSubProducts[0]?.price || p.price) || 0;
          if (displayPrice > 0) {
            list.push({
              ...p,
              subProductsV: validSubProducts,
              displayPrice,
            });
          }
        }
      } else {
        const prodPrice = Number(p.price) || 0;
        if (prodPrice > 0 && matchesLoc(p.localisation)) {
          list.push({
            ...p,
            subProductsV: [],
            displayPrice: prodPrice,
          });
        }
      }
    });

    // Fallback de sécurité : si aucun produit n'est retenu, on affiche tous les produits valides
    if (list.length === 0 && products.length > 0) {
      products.forEach((p) => {
        const displayPrice = Number(p.subProductsV?.[0]?.price || p.price) || 0;
        if (displayPrice > 0) {
          list.push({
            ...p,
            subProductsV: p.subProductsV || [],
            displayPrice
          });
        }
      });
    }

    return list;
  }, [products, locationId, locationName, locations]);

  // Liste des catégories dynamiques réelles de cette boutique
  const availableCategories = useMemo(() => {
    const setCat = new Set(["Tous"]);
    availableStoreProducts.forEach((item) => {
      if (item.category) setCat.add(item.category);
    });
    return Array.from(setCat);
  }, [availableStoreProducts]);

  // Produits filtrés par recherche & catégorie sélectionnée
  const filteredProductsList = useMemo(() => {
    return availableStoreProducts.filter((p) => {
      if (searchTerm) {
        const matchName = p.name.toLowerCase().includes(searchTerm.toLowerCase());
        const matchSub = p.subProductsV?.some((sp) =>
          sp.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
        if (!matchName && !matchSub) return false;
      }

      if (selectedCategory !== "Tous") {
        if (p.category !== selectedCategory) return false;
      }

      return true;
    });
  }, [availableStoreProducts, searchTerm, selectedCategory]);

  // Déduction unitaire du stock (identique à la Caisse Classique Valises.js)
  const processStockDeduction = async (productId, quantity) => {
    try {
      const requestData = {
        products: [
          {
            id: productId,
            quantity: quantity,
          },
        ],
        locationId: locationId || undefined,
      };

      console.log("📤 Déduction stock tactile envoyée:", requestData);

      const response = await axios.post("/api/StockV/reduceStock", requestData, {
        headers: { "Content-Type": "application/json" },
      });

      console.log("📥 Réponse déduction stock tactile:", response.data);

      if (response.data && response.data.results) {
        const result = response.data.results[0];
        console.log("📊 Résultat détaillé:", result);

        const isSuccessful =
          result.success === true ||
          (response.data.message && response.data.message.includes("Déduction effectuée")) ||
          (result.newQuantity !== undefined && result.previousQuantity !== undefined &&
            result.newQuantity < result.previousQuantity);

        if (isSuccessful) {
          console.log(`✅ Stock déduit pour ${productId}`);
          return {
            success: true,
            details: result,
            message: response.data.message || "Déduction réussie"
          };
        } else {
          console.warn(`⚠️ Déduction échouée pour ${productId}:`, result.status || result.error);
          return {
            success: false,
            error: result.status || result.error || "Erreur inconnue",
            details: result
          };
        }
      }

      console.warn(`⚠️ Réponse invalide pour ${productId}:`, response.data);
      return {
        success: false,
        error: "Format de réponse invalide",
        details: response.data
      };
    } catch (error) {
      console.error("❌ Erreur processStockDeduction:", error.response?.data || error.message);
      return {
        success: false,
        error: error.response?.data?.message || error.message
      };
    }
  };

  // Déduction du stock pour l'ensemble des articles du panier
  const processStockDeductions = async () => {
    console.log("🔄 Début de la déduction de stock (Caisse Tactile)...");

    for (const item of cart) {
      const productId = item.subProductId || item.productId || item.id;
      console.log(`📦 Traitement stock tactile: ${item.name} (x${item.quantity}, ID: ${productId})`);

      if (productId) {
        const result = await processStockDeduction(productId, item.quantity);
        if (!result.success) {
          console.error(`❌ Échec déduction stock pour ${item.name}:`, result);
        }
      } else {
        console.error(`❌ ID manquant pour l'article du panier: ${item.name}`);
      }
    }

    console.log("✅ Déduction de stock tactile terminée");
    return true;
  };

  // 7. Validation & Création de Vente (`BillValises`)
  const createBillInDatabase = async () => {
    if (cart.length === 0) {
      alert("Le panier est vide !");
      return null;
    }

    setCheckoutLoading(true);
    try {
      await saveClientData();

      // 🔥 DÉDUCTION DU STOCK (identique à la caisse classique)
      try {
        await processStockDeductions();
      } catch (stockError) {
        console.warn("⚠️ Erreur déduction stock tactile, continuation...:", stockError.message);
      }

      const itemsPayload = cart.map((item) => ({
        id: item.subProductId || item.productId,
        name: item.name,
        price: item.price,
        quantity: item.quantity,
      }));

      const nextRes = await axios.post("/api/BillsValises/generateNextBillNumber", {
        locationId: locationId || locationName,
      });
      const billNumber = nextRes.data?.nextBillNumber || "001";

      const token = localStorage.getItem("token");
      const headers = { Authorised: token };

      const discountLabel = selectedDiscount
        ? selectedDiscount.name
        : (activeDiscountPercentage > 0 ? `Remise ${activeDiscountPercentage}%` : "");

      const billRes = await axios.post(
        "/api/BillsValises/createBillV",
        {
          billNumber,
          items: itemsPayload,
          total: totalTTC,
          totalHT,
          tva,
          discount: discountAmount,
          discountPercentage: activeDiscountPercentage,
          discountName: discountLabel,
          discountId: selectedDiscount?._id || null,
          paymentMethod,
          localisation: locationId || undefined,
          date: new Date(),
        },
        { headers }
      );

      // 🔥 Déclencher le rafraîchissement du stock dans toute l'application
      console.log("🔄 Déclenchement du rafraîchissement du stock...");
      localStorage.setItem('stock-needs-refresh', Date.now().toString());
      window.dispatchEvent(new Event('stock-updated'));
      const saleData = {
        billNumber,
        total: totalTTC,
        subTotal: subTotalTTC,
        discount: discountAmount,
        discountPercentage: activeDiscountPercentage,
        discountName: discountLabel,
        totalHT,
        tva,
        paymentMethod,
        date: new Date(),
        items: [...cart],
        clientName,
        clientPhone,
        clientEmail,
        cashierName: cashier?.name || "Caisse",
        locationName,
      };

      setCompletedSale(saleData);
      return { bill: billRes.data, billNumber, saleData };
    } catch (e) {
      console.error("Erreur création facture :", e);
      alert("Erreur lors de l'enregistrement : " + (e.response?.data?.message || e.message));
      return null;
    } finally {
      setCheckoutLoading(false);
    }
  };

  // Fonction pour télécharger / afficher le PDF officiel de la vente individuelle
  const downloadPdfForSale = async (saleData) => {
    try {
      const response = await axios.post("/api/BillsValises/downloadSalePdf", saleData, {
        responseType: "blob",
      });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch (e) {
      console.error("Erreur téléchargement PDF :", e);
      alert("Erreur lors de la génération du PDF du reçu : " + (e.message || "Erreur serveur"));
    }
  };

  // Action : Génération & Impression du Reçu PDF de la Vente
  const handlePrintReceipt = async () => {
    const result = await createBillInDatabase();
    if (!result) return;

    const { billNumber, saleData } = result;
    await downloadPdfForSale(saleData);

    setStatusMsg(`✅ Facture #${billNumber} enregistrée et Reçu PDF généré !`);
    clearCart();
  };

  // Action : WhatsApp avec Reçu PDF de la Vente
  const handleSendWhatsApp = async () => {
    if (!clientPhone) {
      alert("Veuillez saisir le numéro de téléphone du client");
      return;
    }

    const result = await createBillInDatabase();
    if (!result) return;
    const { billNumber, saleData } = result;

    // Télécharger le PDF de la vente individuelle
    await downloadPdfForSale(saleData);

    let cleanPhone = clientPhone.replace(/\D/g, "");
    if (cleanPhone.length === 8) cleanPhone = "216" + cleanPhone;

    const itemsSummary = (saleData.items || [])
      .map((i) => `  • *${i.name}* x${i.quantity} ──> *${(i.price * i.quantity).toFixed(2)} DT*`)
      .join("\n");

    const activeWhatsapp = cashier?.senderWhatsapp || SENDER_WHATSAPP;

    const discountLine = discountAmount > 0
      ? `\n🏷️ *${saleData.discountName || 'Remise'} (-${activeDiscountPercentage}%) :* -${discountAmount.toFixed(2)} DT\n`
      : "";

    const message = `🧾 *REÇU DE CAISSE OFFICIEL*
📍 *${locationName}*

━━━━━━━━━━━━━━━━━━━━━
🆔 *Ticket N° :* #${billNumber}
📅 *Date :* ${new Date().toLocaleDateString("fr-FR")} ${new Date().toLocaleTimeString("fr-FR", { hour: '2-digit', minute: '2-digit' })}
👤 *Caissier(e) :* ${cashier?.name || 'Caisse'}
👤 *Client :* ${clientName || 'Client'}
💳 *Règlement :* ${paymentMethod}
━━━━━━━━━━━━━━━━━━━━━

🛍️ *DÉTAIL DES ACHATS :*
${itemsSummary}
${discountLine}
━━━━━━━━━━━━━━━━━━━━━
💰 *TOTAL NET RÉGLÉ : ${totalTTC.toFixed(2)} DT*
━━━━━━━━━━━━━━━━━━━━━

📱 *Expéditeur Officiel :* ${activeWhatsapp}
✨ _Merci de votre visite et à très bientôt !_`;

    const waUrl = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`;
    window.open(waUrl, "_blank");

    setStatusMsg(`✅ Facture #${billNumber} enregistrée et Reçu PDF préparé pour WhatsApp (${activeWhatsapp}) !`);
    clearCart();
  };

  // Action : Email Client avec Pièce Jointe PDF de la Vente
  const handleSendEmail = async () => {
    if (!clientEmail) {
      alert("Veuillez saisir l'adresse email du client");
      return;
    }

    const result = await createBillInDatabase();
    if (!result) return;
    const { billNumber, saleData } = result;

    try {
      const res = await axios.post("/api/BillsValises/sendReceiptEmail", {
        clientEmail,
        clientName,
        billNumber,
        total: totalTTC,
        items: cart,
        locationName,
        locationId,
        cashierName: cashier?.name || "Caisse",
        paymentMethod,
        senderEmail: cashier?.senderEmail,
        senderEmailPassword: cashier?.senderEmailPassword,
      });

      if (res.data?.success) {
        setStatusMsg(`✅ Facture #${billNumber} enregistrée & Reçu PDF joint par Email (${clientEmail}) !`);
      } else {
        setStatusMsg(`⚠️ Vente #${billNumber} enregistrée mais erreur email: ${res.data?.message}`);
      }
    } catch (e) {
      setStatusMsg(`⚠️ Vente enregistrée (#${billNumber}), erreur email.`);
    }

    clearCart();
  };

  const getLocationThemeClass = (loc) => {
    if (!loc) return "theme-bng";
    const l = loc.toLowerCase();
    if (l.includes("twin") || l.includes("tp-") || l.includes("tp ") || l.includes("park")) return "theme-twinpark";
    if (l.includes("espace") || l.includes("e-space")) return "theme-espace";
    if (l.includes("bng") || l.includes("bag")) return "theme-bng";
    return "theme-bng";
  };

  return (
    <div className={`pos-mobile-root ${getLocationThemeClass(locationName)}`}>
      {/* ══ ÉCRAN DE CONNEXION CAISSIER ══ */}
      {!isUnlocked ? (
        <div className="pos-login-overlay">
          <div className="pos-login-card">
            <div className="pos-login-header">
              <div className="pos-icon">📱</div>
              <h2>Caisse Mobile Tactile</h2>
              <p>Connectez-vous avec votre <strong>Code PIN à 4 chiffres</strong></p>
            </div>

            <form onSubmit={handleCashierLogin}>
              <div className="pos-form-group">
                <label>Nom du Caissier / Caissière</label>
                <input
                  type="text"
                  placeholder="Saisissez votre nom"
                  value={loginName}
                  onChange={(e) => setLoginName(e.target.value)}
                />
              </div>

              <div className="pos-form-group">
                <label>Code PIN (4 chiffres)</label>
                <input
                  type="password"
                  maxLength={4}
                  placeholder="• • • •"
                  value={pinInput}
                  onChange={(e) => setPinInput(e.target.value.replace(/\D/g, "").slice(0, 4))}
                  className="pin-input-big"
                  autoFocus
                />
              </div>

              <div className="pin-keypad">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => pinInput.length < 4 && setPinInput(pinInput + n)}
                  >
                    {n}
                  </button>
                ))}
                <button type="button" onClick={() => setPinInput("")} className="btn-key-clear">
                  C
                </button>
                <button
                  type="button"
                  onClick={() => pinInput.length < 4 && setPinInput(pinInput + "0")}
                >
                  0
                </button>
                <button
                  type="button"
                  onClick={() => setPinInput(pinInput.slice(0, -1))}
                  className="btn-key-back"
                >
                  ⌫
                </button>
              </div>

              {loginError && <div className="pos-login-error">❌ {loginError}</div>}

              <button
                type="submit"
                disabled={pinInput.length !== 4}
                className={`pos-login-btn ${pinInput.length === 4 ? "active" : ""}`}
              >
                🔓 Ouvrir ma Caisse
              </button>
            </form>
          </div>
        </div>
      ) : (
        /* ══ INTERFACE DE CAISSE MOBILE TACTILE ══ */
        <div className="pos-app-layout">
          <header className="pos-top-bar">
            <div className="pos-brand">
              <span className="badge-shop">📍 {locationName}</span>
              <span className="badge-user">👤 {cashier?.name || "Caissier"}</span>
            </div>
            <button onClick={handleLockPOS} className="btn-lock-pos">
              🔒 Verrouiller
            </button>
          </header>

          <div className="pos-main-container">
            {/* Colonne Gauche : Recherche, Catégories & Grille Produits avec Images */}
            <div className="pos-products-section">
              <div className="pos-filter-bar">
                <input
                  type="text"
                  placeholder="🔍 Rechercher un produit, valise, sous-produit..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pos-search-input"
                />
                <div className="pos-categories-wrapper">
                  <button type="button" onClick={() => scrollCat(-1)} className="btn-scroll-cat">
                    ◀
                  </button>
                  <div className="pos-categories-pills" ref={categoriesRef}>
                    {availableCategories.map((cat) => (
                      <button
                        key={cat}
                        className={`cat-pill ${selectedCategory === cat ? "active" : ""}`}
                        onClick={() => setSelectedCategory(cat)}
                      >
                        {cat}
                      </button>
                    ))}
                  </div>
                  <button type="button" onClick={() => scrollCat(1)} className="btn-scroll-cat">
                    ▶
                  </button>
                </div>
              </div>

              {/* Grille Tactile avec Images & Variantes */}
              {loading ? (
                <div className="pos-loading">Chargement des articles de la boutique...</div>
              ) : filteredProductsList.length === 0 ? (
                <div className="pos-empty-grid">Aucun produit trouvé pour la boutique 📍 {locationName}.</div>
              ) : (
                <div className="pos-grid">
                  {filteredProductsList.map((p) => {
                    const hasVariants = p.subProductsV && p.subProductsV.length > 1;
                    const singleSub = p.subProductsV && p.subProductsV.length === 1 ? p.subProductsV[0] : null;
                    const imgSrc = getImageSrc(singleSub?.image || p.image, p._id);

                    return (
                      <div
                        key={p._id}
                        className="pos-product-card"
                        onClick={() => {
                          if (hasVariants) {
                            setSelectedParentProduct(p);
                          } else {
                            addToCart(p, singleSub);
                          }
                        }}
                      >
                        <div className="pos-card-img-wrapper">
                          {imgSrc ? (
                            <img src={imgSrc} alt={p.name} className="pos-card-img" />
                          ) : (
                            <div className="pos-img-placeholder">🛍️</div>
                          )}
                          <span className="pos-card-cat">{p.category}</span>
                        </div>

                        <div className="pos-card-body">
                          <div className="pos-prod-name">
                            {p.name} {singleSub ? `(${singleSub.name})` : ""}
                          </div>
                          <div className="pos-prod-footer">
                            <span className="pos-prod-price">
                              {p.displayPrice.toFixed(2)} DT
                            </span>
                            {hasVariants ? (
                              <span className="badge-variant">
                                {p.subProductsV.length} tailles/variantes ➔
                              </span>
                            ) : (
                              <button className="btn-add-quick">+</button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Colonne Droite : Panier & Règlement */}
            <div className="pos-cart-section" id="pos-cart-panel">
              <h3>🛒 Panier & Règlement</h3>

              {statusMsg && <div className="pos-status-alert">{statusMsg}</div>}

              <div className="pos-cart-items">
                {cart.length === 0 ? (
                  <p className="cart-empty-text">Panier vide. Touchez un article pour l'ajouter.</p>
                ) : (
                  cart.map((item) => (
                    <div key={item.id} className="cart-item-row">
                      <div className="cart-item-info">
                        <div className="cart-item-title">{item.name}</div>
                        <div className="cart-item-sub">
                          {item.price.toFixed(2)} DT x {item.quantity} ={" "}
                          <strong>{(item.price * item.quantity).toFixed(2)} DT</strong>
                        </div>
                      </div>
                      <div className="cart-item-controls">
                        <button onClick={() => updateQuantity(item.id, -1)}>-</button>
                        <span>{item.quantity}</span>
                        <button onClick={() => updateQuantity(item.id, 1)}>+</button>
                        <button onClick={() => removeFromCart(item.id)} className="btn-trash">
                          🗑️
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Sélecteur de Remises & Promotions par Localisation */}
              <div className="pos-discounts-box">
                <div className="pos-discounts-header">
                  <span>🏷️ Remises & Promotions</span>
                  {activeDiscountPercentage > 0 && (
                    <span className="pos-discount-active-badge">
                      -{activeDiscountPercentage}% (-{discountAmount.toFixed(2)} DT)
                    </span>
                  )}
                </div>

                <div className="pos-discount-chips">
                  {availableDiscounts.length === 0 ? (
                    <small style={{ color: "#64748b", fontStyle: "italic", fontSize: "0.82rem" }}>
                      Aucune remise active pour cet emplacement.
                    </small>
                  ) : (
                    availableDiscounts.map((d) => {
                      const isSelected = selectedDiscount?._id === d._id;
                      return (
                        <button
                          key={d._id}
                          type="button"
                          className={`pos-discount-chip ${d.type === 'permanent' ? 'chip-fidele' : 'chip-promo'} ${isSelected ? 'selected' : ''}`}
                          onClick={() => {
                            if (isSelected) {
                              setSelectedDiscount(null);
                            } else {
                              setSelectedDiscount(d);
                              setCustomDiscountPct(0);
                            }
                          }}
                          title={d.name}
                        >
                          {d.type === 'permanent' ? '🌟' : '🎉'} {d.name} ({d.percentage}%) {isSelected ? '✓' : ''}
                        </button>
                      );
                    })
                  )}
                </div>
              </div>

              <div className="pos-cart-summary">
                <div className="summary-line">
                  <span>Sous-Total TTC</span>
                  <span>{subTotalTTC.toFixed(2)} DT</span>
                </div>
                {discountAmount > 0 && (
                  <div className="summary-line discount-line">
                    <span className="text-danger fw-bold">
                      🏷️ {selectedDiscount ? selectedDiscount.name : `Remise ${activeDiscountPercentage}%`}
                    </span>
                    <span className="text-danger fw-bold">- {discountAmount.toFixed(2)} DT</span>
                  </div>
                )}
                <div className="summary-line">
                  <span>Total HT</span>
                  <span>{totalHT.toFixed(2)} DT</span>
                </div>
                <div className="summary-line">
                  <span>TVA (7%)</span>
                  <span>{tva.toFixed(2)} DT</span>
                </div>
                <div className="summary-line total-highlight">
                  <span>NET À PAYER TTC</span>
                  <span>{totalTTC.toFixed(2)} DT</span>
                </div>
              </div>

              <div className="pos-client-box">
                <h4>👤 Informations Client (Optionnel)</h4>
                {clientStatus && <div className="client-status-tag">{clientStatus}</div>}

                <input
                  type="text"
                  placeholder="📞 N° Téléphone (ex: 55123456)"
                  value={clientPhone}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  className="pos-input-sm"
                />
                <input
                  type="text"
                  placeholder="Nom du Client"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  className="pos-input-sm"
                />
                <input
                  type="email"
                  placeholder="📧 Email Client (pour envoi direct)"
                  value={clientEmail}
                  onChange={(e) => setClientEmail(e.target.value)}
                  className="pos-input-sm"
                />
              </div>

              <div className="pos-payment-mode">
                <label>Mode de Règlement :</label>
                <div className="mode-buttons">
                  <button
                    className={paymentMethod === "Espece" ? "active" : ""}
                    onClick={() => setPaymentMethod("Espece")}
                  >
                    💵 Espèce
                  </button>
                  <button
                    className={paymentMethod === "TPE" ? "active" : ""}
                    onClick={() => setPaymentMethod("TPE")}
                  >
                    💳 Carte / TPE
                  </button>
                </div>
              </div>

              <div className="pos-checkout-actions">
                <button
                  onClick={handlePrintReceipt}
                  disabled={cart.length === 0 || checkoutLoading}
                  className="btn-action-print"
                >
                  🖨️ Valider & Imprimer
                </button>
                <button
                  onClick={handleSendWhatsApp}
                  disabled={cart.length === 0 || checkoutLoading}
                  className="btn-action-wa"
                >
                  💬 WhatsApp ({SENDER_WHATSAPP})
                </button>
                <button
                  onClick={handleSendEmail}
                  disabled={cart.length === 0 || checkoutLoading}
                  className="btn-action-email"
                >
                  📧 Envoyer par Email
                </button>
              </div>
            </div>
          </div>

          {/* Bouton Flottant Mobile pour Accès Rapide au Panier */}
          {cart.length > 0 && (
            <div className="pos-mobile-floating-cart-bar">
              <button
                type="button"
                onClick={() => {
                  document.getElementById('pos-cart-panel')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="btn-floating-cart"
              >
                🛒 Panier ({cart.reduce((s, i) => s + i.quantity, 0)}) • {totalTTC.toFixed(2)} DT ➔
              </button>
            </div>
          )}
        </div>
      )}

      {/* ══ MODAL DE SÉLECTION DES VARIANTES / SOUS-PRODUITS ══ */}
      {selectedParentProduct && (
        <div className="variant-modal-overlay" onClick={() => setSelectedParentProduct(null)}>
          <div className="variant-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="variant-modal-header">
              <h3>📦 Choisir la taille / variante de : {selectedParentProduct.name}</h3>
              <button className="btn-close-modal" onClick={() => setSelectedParentProduct(null)}>
                ✕
              </button>
            </div>
            <div className="variant-grid">
              {selectedParentProduct.subProductsV?.map((sp) => {
                const subImg = getImageSrc(sp.image || selectedParentProduct.image, sp._id);
                return (
                  <div
                    key={sp._id}
                    className="variant-tile"
                    onClick={() => addToCart(selectedParentProduct, sp)}
                  >
                    <div className="variant-img-area">
                      {subImg ? (
                        <img src={subImg} alt={sp.name} className="variant-img" />
                      ) : (
                        <div className="variant-placeholder">🏷️</div>
                      )}
                    </div>
                    <div className="variant-info">
                      <div className="variant-name">{sp.name}</div>
                      <div className="variant-price">{(Number(sp.price) || 0).toFixed(2)} DT</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
      {/* ══ MODAL DE CONFIRMATION DE VENTE ET GÉNÉRATION DU TICKET ══ */}
      {completedSale && (
        <div className="sale-confirm-overlay" onClick={() => setCompletedSale(null)}>
          <div className="sale-confirm-card" onClick={(e) => e.stopPropagation()}>
            <div className="sale-confirm-header">
              <div className="confirm-icon">🎉</div>
              <h3>Vente Enregistrée & Ticket Généré !</h3>
              <p className="confirm-bill-tag">Ticket N° <strong>#{completedSale.billNumber}</strong></p>
            </div>

            <div className="sale-confirm-details">
              <div className="confirm-row">
                <span>📍 Boutique :</span>
                <strong>{completedSale.locationName}</strong>
              </div>
              <div className="confirm-row">
                <span>👤 Caissier(e) :</span>
                <strong>{completedSale.cashierName}</strong>
              </div>
              <div className="confirm-row">
                <span>💵 Mode de Règlement :</span>
                <strong>{completedSale.paymentMethod}</strong>
              </div>
              {completedSale.discount > 0 && (
                <div className="confirm-row" style={{ color: "#ef4444" }}>
                  <span>🏷️ {completedSale.discountName || 'Remise'} :</span>
                  <strong>-{(completedSale.discount || 0).toFixed(2)} DT (-{completedSale.discountPercentage}%)</strong>
                </div>
              )}
              <div className="confirm-row highlight-total">
                <span>TOTAL NET RÉGLÉ :</span>
                <strong>{(completedSale.total || 0).toFixed(2)} DT</strong>
              </div>

              <div className="confirm-items-list">
                <h4>Articles Vendus ({completedSale.items?.length || 0}) :</h4>
                {completedSale.items?.map((item, idx) => (
                  <div key={idx} className="confirm-item-line">
                    <span>• {item.name} x{item.quantity}</span>
                    <span>{(item.price * item.quantity).toFixed(2)} DT</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="sale-confirm-actions">
              <div style={{
                background: "rgba(16, 185, 129, 0.15)",
                border: "1px solid #10b981",
                borderRadius: "12px",
                padding: "10px",
                marginBottom: "12px",
                textAlign: "center"
              }}>
                <div style={{ color: "#10b981", fontWeight: "700", fontSize: "0.88rem" }}>
                  📡 Prêt pour scannage NFC Client
                </div>
                <div style={{ color: "#94a3b8", fontSize: "0.78rem", marginTop: "2px" }}>
                  Le client peut maintenant approcher son téléphone du tag NFC de la caisse.
                </div>
                {locationId && (
                  <a
                    href={`/caisse-nfc/${locationId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: "inline-block",
                      marginTop: "6px",
                      fontSize: "0.78rem",
                      color: "#38bdf8",
                      textDecoration: "underline"
                    }}
                  >
                    👁️ Tester / Ouvrir le portail client NFC
                  </a>
                )}
              </div>

              <button
                type="button"
                onClick={() => downloadPdfForSale(completedSale)}
                className="btn-download-pdf"
                style={{
                  width: "100%",
                  padding: "12px",
                  borderRadius: "14px",
                  border: "none",
                  background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
                  color: "#ffffff",
                  fontWeight: "800",
                  fontSize: "1rem",
                  marginBottom: "10px",
                  cursor: "pointer"
                }}
              >
                📄 Télécharger Reçu PDF (#{completedSale.billNumber})
              </button>
              <button
                type="button"
                onClick={() => setCompletedSale(null)}
                className="btn-next-sale"
              >
                ⚡ Nouvelle Vente (Fermer)
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CaisseMobile;
